/**
 * Imperative arrange playhead + edit-cursor lines.
 * Audio / tracker ticks only touch `transform` / classList, never React.
 */
import { patternSteps, type Pattern } from "../../../../src/lib/audio";

export const ARRANGE_HEADER_PX = 33;
export const ARRANGE_ROW_PX = 42;

/** Gold row background while a pattern is under the playhead. */
export const ARRANGE_PLAYING_BG = "#2a261a";

export type ArrangeLineInfo = {
  patternId: string;
  row: number;
} | null;

/** @deprecated Use ArrangeLineInfo */
export type ArrangePlayheadInfo = ArrangeLineInfo;

type ArrangePlayheadHost = {
  playheadLine: HTMLElement;
  cursorLine: HTMLElement;
  /** Pattern matrix grid — used for imperative gold-row highlight. */
  matrix: HTMLElement;
  /** Current arrangement patterns (read live — do not capture a stale array). */
  getPatterns: () => Pattern[];
};

let host: ArrangePlayheadHost | null = null;
let lastPlayhead: ArrangeLineInfo = null;
let lastCursor: ArrangeLineInfo = null;
let lastPlayingPatternId: string | null = null;

export function setArrangePlayheadHost(next: ArrangePlayheadHost | null): void {
  if (!next) {
    clearPlayingHighlight();
    lastPlayingPatternId = null;
    host = null;
    return;
  }
  host = next;
  applyLine(next.playheadLine, lastPlayhead, next.getPatterns);
  applyLine(next.cursorLine, lastCursor, next.getPatterns);
  lastPlayingPatternId = null;
  applyPlayingHighlight(lastPlayhead?.patternId ?? null);
}

/** Call from the audio onPlayhead path — mutates the playhead DOM only. */
export function syncArrangePlayhead(info: ArrangeLineInfo): void {
  lastPlayhead = info;
  const current = host;
  if (!current) {
    return;
  }
  applyLine(current.playheadLine, info, current.getPatterns);
  applyPlayingHighlight(info?.patternId ?? null);
}

/** Pattern currently under the arrange playhead, or null when stopped. */
export function getArrangePlayingPatternId(): string | null {
  return lastPlayhead?.patternId ?? null;
}

/**
 * Re-apply gold-row highlight after a React render wiped classList.
 * Call from ArrangeBody layout effects — not from the audio hot path.
 */
export function restoreArrangePlayingHighlight(): void {
  // Force re-paint even when the pattern id is unchanged.
  lastPlayingPatternId = null;
  applyPlayingHighlight(lastPlayhead?.patternId ?? null);
}

/** Call when the tracker edit cursor (or active pattern) moves. Always visible while host is up. */
export function syncArrangeEditCursor(info: ArrangeLineInfo): void {
  lastCursor = info;
  const current = host;
  if (!current) {
    return;
  }
  applyLine(current.cursorLine, info, current.getPatterns);
}

const LINE_VISIBLE_OPACITY = "0.7";

function applyLine(
  line: HTMLElement,
  info: ArrangeLineInfo,
  getPatterns: () => Pattern[],
): void {
  if (!info) {
    line.style.opacity = "0";
    return;
  }
  const y = lineY(getPatterns(), info.patternId, info.row);
  if (y === null) {
    line.style.opacity = "0";
    return;
  }
  line.style.opacity = LINE_VISIBLE_OPACITY;
  line.style.transform = `translate3d(0, ${y}px, 0)`;
}

function applyPlayingHighlight(patternId: string | null): void {
  if (patternId === lastPlayingPatternId) {
    return;
  }
  clearPlayingHighlight();
  lastPlayingPatternId = patternId;
  if (!patternId || !host) {
    return;
  }
  const cells = host.matrix.querySelectorAll<HTMLElement>(
    `[data-arrange-pattern="${patternId}"]`,
  );
  for (const el of cells) {
    el.classList.add("is-arrange-playing");
    el.style.backgroundColor = ARRANGE_PLAYING_BG;
  }
}

function clearPlayingHighlight(): void {
  const root = host?.matrix;
  if (!root) {
    return;
  }
  const cells = root.querySelectorAll<HTMLElement>(".is-arrange-playing");
  for (const el of cells) {
    el.classList.remove("is-arrange-playing");
    const base = el.dataset.arrangeBg;
    if (base) {
      el.style.backgroundColor = base;
    } else {
      el.style.removeProperty("background-color");
    }
  }
}

function lineY(patterns: Pattern[], patternId: string, row: number): number | null {
  const index = patterns.findIndex(pattern => pattern.id === patternId);
  if (index < 0) {
    return null;
  }
  const pattern = patterns[index]!;
  const steps = Math.max(1, patternSteps(pattern));
  const clamped = Math.max(0, Math.min(steps, row));
  // Progress through this pattern's row band (top → bottom).
  return ARRANGE_HEADER_PX + index * ARRANGE_ROW_PX + (clamped / steps) * ARRANGE_ROW_PX;
}
